import React, { useCallback, useContext, useMemo } from "react";
import MissionDetails from "../../components/editors/MissionDetails";
import { useQuery } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useI18n } from "../../hooks/i18n-hooks";
import {
    ContractSignatureStatus,
    ContractState,
    EarningType,
    ProfileType,
} from "../../__generated__/graphql";
import { GET_MISSION } from "../../graphql/core";
import {
    useMissionDisplayAttributes,
    useMissionHolidayBalance,
    useMissionOvertimeBalance,
} from "../../hooks/missions";
import Loading from "../../components/Loading";
import ScreenHeader, { MenuAction } from "./ScreenHeader";
import { openPhoneNumber } from "../../utils";
import { useAddMissionToCalendar } from "../../hooks/calendar";
import ScreenCard, { ButtonType } from "../ScreenCard";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";

type Props = StackScreenProps<RootStackParamList, "MissionDetails">;

const MissionDetailsScreen: React.FC<Props> = ({ route, navigation }) => {
    const i18n = useI18n();
    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const { loading, error, data, refetch } = useQuery(GET_MISSION, {
        variables: { id: route?.params?.missionId || "" },
        fetchPolicy: "network-only",
    });

    const addMissionToCalendar = useAddMissionToCalendar();

    const mission = data?.getMission;

    const [otherName, otherPhone] = useMemo(() => {
        if (mission) {
            if (profileType === ProfileType.Client) {
                return [mission.workerInfo.name, mission.workerInfo.phoneNumber];
            }
            if (profileType === ProfileType.Worker) {
                return [mission.clientInfo.name, mission.clientInfo.phoneNumber];
            }
        }
        return ["", ""];
    }, [mission, profileType]);

    const getMissionDisplayAttributes = useMissionDisplayAttributes();
    const actions = useMemo(() => {
        if (!mission || loading) {
            // do not display menu if we don't have a mission or a new one is loading
            return [];
        }
        const result: MenuAction[] = [
            {
                label: i18n.t("mission.contact_other", { name: otherName }),
                icon: "phone-call",
                onPress: () => {
                    otherPhone && void openPhoneNumber(otherPhone);
                },
            },
            ...(mission?.currentContract?.state &&
            ![ContractState.Cancelled, ContractState.Terminated, ContractState.Ended].includes(
                mission?.currentContract?.state,
            )
                ? [
                      {
                          label: i18n.t("mission.add_to_calendar"),
                          icon: "calendar" as const,
                          onPress: () => {
                              mission && void addMissionToCalendar(mission);
                          },
                      },
                  ]
                : []),
        ];
        const { canCancel } = getMissionDisplayAttributes(mission, profileType);

        if (canCancel) {
            result.push({
                label: i18n.t("mission.cancel_mission"),
                icon: "cancel-mission",
                color: "red",
                onPress: () => {
                    mission &&
                        navigation?.navigate("MissionTermination", {
                            missionId: mission.id,
                        });
                },
            });
        }
        return result;
    }, [
        addMissionToCalendar,
        getMissionDisplayAttributes,
        i18n,
        loading,
        mission,
        navigation,
        otherName,
        otherPhone,
        profileType,
    ]);

    const isPendingSignatureByCurrentUser = useMemo(() => {
        return (
            // we may also enable the button if the status is in "ContractSignatureStatus.WaitingSelfSign"
            // ContractSignatureStatus.WaitingSelfSign means that the contract has already a signature request
            // that has not been signed by the current user
            [
                ContractSignatureStatus.NeedSelfSign,
                ContractSignatureStatus.WaitingSelfSign,
            ].includes(
                mission?.currentContract?.signatureStatus ||
                    ContractSignatureStatus.WaitingContract,
            )
        );
    }, [mission?.currentContract?.signatureStatus]);

    const { refetch: holidayBalanceRefetch, balance: holidayBalance } = useMissionHolidayBalance(
        mission?.currentContract?.earningType !== EarningType.Package ? mission?.id : undefined,
    );
    // overtime is only available for monthly missions
    const { refetch: overtimeBalanceRefetch, balance: overtimeBalance } = useMissionOvertimeBalance(
        mission?.currentContract?.earningType === EarningType.Monthly ? mission?.id : undefined,
    );

    const fullRefetch = useCallback(async () => {
        await refetch();
        await holidayBalanceRefetch();
        await overtimeBalanceRefetch();
    }, [holidayBalanceRefetch, refetch, overtimeBalanceRefetch]);

    const buttons = useMemo<ButtonType[]>(() => {
        return mission && isPendingSignatureByCurrentUser
            ? [
                  {
                      title: i18n.t("screens.user.missions.read_and_sign_contract"),
                      onPress: () => {
                          navigation?.navigate("ContractPreviewScreen", {
                              contractId: mission.currentContract?.id || "",
                          });
                      },
                      mode: "secondary",
                  },
              ]
            : [];
    }, [i18n, isPendingSignatureByCurrentUser, mission, navigation]);

    return (
        <>
            <ScreenHeader
                useSmallLabel
                onLeftAction="back"
                label={i18n.t("mission.nav_title")}
                hideProfile
                actions={actions}
            ></ScreenHeader>
            <ScreenCard
                shrinkButtonToIcon="signature"
                slideButtonUp
                refresh={fullRefetch}
                buttons={buttons}
                automaticallyAdjustKeyboardInsets={false}
                headband={
                    error
                        ? {
                              text: i18n.t("screens.user.missions.cannot_load_missions"),
                              mode: "error",
                          }
                        : undefined
                }
            >
                {!mission && loading && <Loading />}
                {mission && profileType && (
                    <MissionDetails
                        mission={mission}
                        navigation={navigation}
                        profileType={profileType}
                        holidayBalance={holidayBalance}
                        overtimeBalance={overtimeBalance}
                    />
                )}
            </ScreenCard>
        </>
    );
};

export default MissionDetailsScreen;
